import { useRef, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { cx } from '@/lib/cx';
import { EM_DASH } from '@/lib/format/number';
import { t } from '@/lib/i18n';
import styles from './ScoreGrid.module.css';
import { CheckIcon, CloseIcon } from '@/components/icons';

export interface GridHole {
  number: number;
  par: number;
  strokes: number | null;
  putts: number | null;
  fairwayHit: boolean | null;
  greenInRegulation: boolean | null;
}

interface ScoreGridProps {
  holes: GridHole[];
  onChange: (holeNumber: number, patch: { strokes?: number; putts?: number }) => void;
  /** Fired when a cell loses focus, so the row can be pushed to the server. */
  onCommit: (holeNumber: number) => void;
  onExit: () => void;
  onReview: () => void;
  filledCount: number;
}

/**
 * The whole card, editable, for desktop entry.
 *
 * Keyboard behaviour is the point: Tab walks the strokes row, Enter jumps to the next hole,
 * and each cell saves when it loses focus. Anything that forces a mouse round-trip per hole
 * would be slower than the phone flow it is meant to beat.
 */
export function ScoreGrid({
  holes,
  onChange,
  onCommit,
  onExit,
  onReview,
  filledCount,
}: ScoreGridProps) {
  const strokeRefs = useRef(new Map<number, HTMLInputElement>());

  const onStrokesKey = (e: KeyboardEvent<HTMLInputElement>, holeNumber: number) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    onCommit(holeNumber);
    strokeRefs.current.get(holeNumber + 1)?.focus();
    strokeRefs.current.get(holeNumber + 1)?.select();
  };

  const segments = segmentsFor(holes);

  return (
    <div className={styles.wrap}>
      <div className={styles.scroller}>
        <div className={styles.grid}>
          <div className={styles.labels}>
            <div className={styles.headLabel}>{t('scorecard.hole')}</div>
            <div className={cx(styles.cell, styles.labelCell, styles.rowPar)}>
              {t('scorecard.par')}
            </div>
            <div
              className={cx(styles.cell, styles.labelCell, styles.rowStrokes, styles.strokesLabel)}
            >
              {t('scorecard.strokes')}
            </div>
            <div className={cx(styles.cell, styles.labelCell, styles.rowPutts)}>
              {t('scorecard.putts')}
            </div>
            <div className={cx(styles.cell, styles.labelCell, styles.rowDerived)}>
              {t('scorecard.fw')}
            </div>
            <div className={cx(styles.cell, styles.labelCell, styles.rowDerived, styles.last)}>
              {t('scorecard.gir')}
            </div>
          </div>

          {holes.map((h) => (
            <div key={h.number} className={styles.col}>
              <div className={cx(styles.cell, styles.head)}>{h.number}</div>
              <div className={cx(styles.cell, styles.rowPar)}>{h.par}</div>
              <div className={cx(styles.cell, styles.rowStrokes)}>
                <input
                  ref={(el) => {
                    if (el) strokeRefs.current.set(h.number, el);
                    else strokeRefs.current.delete(h.number);
                  }}
                  className={cx(styles.input, styles.inputStrokes)}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={20}
                  value={h.strokes ?? ''}
                  placeholder={String(h.par)}
                  aria-label={t('record.grid.strokesAria', { number: h.number, par: h.par })}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (e.target.value === '') return;
                    if (Number.isFinite(v) && v >= 1 && v <= 20) {
                      onChange(h.number, { strokes: v });
                    }
                  }}
                  onKeyDown={(e) => onStrokesKey(e, h.number)}
                  onBlur={() => onCommit(h.number)}
                  onFocus={(e) => e.target.select()}
                />
              </div>
              <div className={cx(styles.cell, styles.rowPutts)}>
                <input
                  className={styles.input}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={h.strokes ?? 20}
                  value={h.putts ?? ''}
                  aria-label={t('record.grid.puttsAria', { number: h.number })}
                  onChange={(e) => {
                    if (e.target.value === '') return;
                    const v = Number(e.target.value);
                    if (Number.isFinite(v) && v >= 0) onChange(h.number, { putts: v });
                  }}
                  onBlur={() => onCommit(h.number)}
                  onFocus={(e) => e.target.select()}
                />
              </div>
              <div className={cx(styles.cell, styles.rowDerived)}>
                <span className={h.fairwayHit ? styles.hit : styles.miss}>
                  {h.par === 3 || h.fairwayHit == null ? (
            EM_DASH
          ) : h.fairwayHit ? (
            <CheckIcon size={12} />
          ) : (
            <CloseIcon size={12} />
          )}
                </span>
              </div>
              <div className={cx(styles.cell, styles.rowDerived, styles.last)}>
                <span className={h.greenInRegulation ? styles.hit : styles.miss}>
                  {h.greenInRegulation == null ? (
            EM_DASH
          ) : h.greenInRegulation ? (
            <CheckIcon size={12} />
          ) : (
            <CloseIcon size={12} />
          )}
                </span>
              </div>
            </div>
          ))}

          {segments.map((seg) => {
            const par = total(holes, seg.from, seg.to, (h) => h.par);
            const strokes = total(holes, seg.from, seg.to, (h) => h.strokes);
            const putts = total(holes, seg.from, seg.to, (h) => h.putts);
            return (
              <div key={seg.key} className={cx(styles.col, styles.colTotal)}>
                <div className={cx(styles.cell, styles.head)}>{seg.key}</div>
                <div className={cx(styles.cell, styles.rowPar, styles.totalValue)}>{par}</div>
                <div className={cx(styles.cell, styles.rowStrokes, styles.totalValue)}>
                  {strokes ?? EM_DASH}
                </div>
                <div className={cx(styles.cell, styles.rowPutts, styles.totalValue)}>
                  {putts ?? EM_DASH}
                </div>
                <div className={cx(styles.cell, styles.rowDerived)}>{EM_DASH}</div>
                <div className={cx(styles.cell, styles.rowDerived, styles.last)}>{EM_DASH}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.legendCell} aria-hidden="true">
              5
            </span>
            {t('record.grid.focusedCell')}
          </span>
          <span>{t('record.grid.derivedNote')}</span>
          <span className={styles.legendItem}>
            <span className={styles.savedDot} aria-hidden="true" />
            {t('record.grid.savesOnBlur')}
          </span>
          <span>{t('record.grid.filled', { filled: filledCount, total: holes.length })}</span>
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onExit}>
            {t('record.grid.oneAtATime')}
          </Button>
          <Button onClick={onReview}>{t('record.review')}</Button>
        </div>
      </div>
    </div>
  );
}

function segmentsFor(holes: GridHole[]) {
  if (holes.length <= 9) return [{ key: 'TOT', from: 1, to: holes.length }];
  const half = Math.floor(holes.length / 2);
  return [
    { key: 'OUT', from: 1, to: half },
    { key: 'IN', from: half + 1, to: holes.length },
    { key: 'TOT', from: 1, to: holes.length },
  ];
}

function total(
  holes: GridHole[],
  from: number,
  to: number,
  pick: (h: GridHole) => number | null,
): number | null {
  let sum = 0;
  let any = false;
  for (const h of holes) {
    if (h.number < from || h.number > to) continue;
    const v = pick(h);
    if (v == null) continue;
    sum += v;
    any = true;
  }
  return any ? sum : null;
}
